import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];
  #flights = new Set();
  #pulses = new Set();
  #connectionGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#connectionGeneration++;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connectionGeneration++;
    for (const flight of this.#flights) flight.remove();
    for (const pulse of this.#pulses) pulse.cancel();
    this.#flights.clear();
    this.#pulses.clear();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    const generation = this.#connectionGeneration;
    const origin = event.target instanceof Element ? event.target : null;
    const button = origin?.querySelector('.upupa-add__btn, [ref="addToCartButton"]');
    const image = origin?.closest('.upupa-card')?.querySelector('.upupa-kuva img')?.currentSrc
      || origin?.querySelector('add-to-cart-component')?.dataset.productVariantMedia;
    const quantity = event.lines?.reduce((total, line) => total + Number(line.quantity || 0), 0) || 1;
    event.promise
      ?.then(({ cart, detail }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        if (!this.isConnected || generation !== this.#connectionGeneration) return;
        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
        if (event.action === 'add' && !detail?.didError) {
          this.#confirmAdd(button, image, quantity);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };

  #isVisible(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
      && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  #pulseCart() {
    const icon = this.querySelector('.header-actions__cart-icon');
    if (!this.isConnected || !this.#isVisible(icon)) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const orange = getComputedStyle(icon).getPropertyValue('--upupa-orange-rgb').trim();
    const glow = orange ? `rgba(${orange}, 0.3)` : 'transparent';
    const frames = reduced
      ? [{ boxShadow: `0 0 0 5px ${glow}` }, { boxShadow: '0 0 0 0 transparent' }]
      : [
          { transform: 'scale(1)', boxShadow: '0 0 0 0 transparent' },
          { transform: 'scale(1.18)', boxShadow: `0 0 0 7px ${glow}`, offset: 0.4 },
          { transform: 'scale(1)', boxShadow: '0 0 0 0 transparent' },
        ];
    for (const pulse of this.#pulses) pulse.cancel();
    this.#pulses.clear();
    const pulse = icon.animate(frames, { duration: reduced ? 180 : 380, easing: 'ease-out' });
    this.#pulses.add(pulse);
    pulse.finished.catch(() => {}).finally(() => this.#pulses.delete(pulse));
  }

  #confirmAdd(button, image, quantity) {
    if (this.dataset.upupaCartFeedback !== 'true') return;
    const icon = this.querySelector('.header-actions__cart-icon');
    if (!this.#isVisible(icon)) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !this.#isVisible(button) || !customElements.get('fly-to-cart')) {
      this.#pulseCart();
      return;
    }
    const flight = document.createElement('fly-to-cart');
    flight.classList.add('fly-to-cart--quick', 'upupa-cart-flight');
    flight.setAttribute('aria-hidden', 'true');
    flight.source = button;
    flight.destination = icon;
    if (image) {
      flight.style.backgroundImage = `url(${JSON.stringify(image)})`;
    } else {
      flight.textContent = `+${quantity}`;
    }
    const clean = () => {
      this.#flights.delete(flight);
      flight.remove();
    };
    flight.addEventListener('animationend', (event) => {
      if (event.animationName !== 'travel-scale') return;
      clean();
      this.#pulseCart();
    });
    flight.addEventListener('animationcancel', clean, { once: true });
    this.#flights.add(flight);
    document.body.appendChild(flight);
  }

}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
